import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, articles, categories, latestUpdates } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);
  const comic = comics.find((c) => c.id === articleId);

  if (!article && !comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的内容不存在或已被移除</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors no-underline"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (article) {
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.coverImage,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.publishDate,
      dateModified: article.publishDate,
      publisher: {
        '@type': 'Organization',
        name: '无遮韩国免费漫画大全',
        logo: { '@type': 'ImageObject', url: 'https://manhwa-free.com/logo.png' },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `https://manhwa-free.com/article/${article.id}` },
    };

    const cat = categories.find((c) => c.slug === article.category);

    return (
      <>
        <SEOHead
          title={article.title}
          description={article.excerpt}
          keywords={`${article.title},韩漫推荐,韩漫资讯,免费在线阅读韩漫,韩漫漫画网站`}
          canonicalUrl={`/article/${article.id}`}
          ogType="article"
          ogImage={article.coverImage}
          articlePublishedTime={article.publishDate}
          articleModifiedTime={article.publishDate}
        />
        <SchemaOrg type="Organization" />
        <SchemaOrg type="WebSite" />

        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <article>
              <div className="w-full px-4 md:px-6 py-6">
                <div className="max-w-3xl mx-auto">
                  <Breadcrumb
                    items={[
                      { label: '首页', href: '/' },
                      { label: '韩漫资讯', href: '/category/all' },
                      { label: article.title },
                    ]}
                  />
                </div>
              </div>

              <div className="w-full px-4 md:px-6">
                <div className="max-w-3xl mx-auto">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3 flex-wrap">
                      {cat && (
                        <Link
                          to={`/category/${cat.slug}`}
                          className="px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full font-medium no-underline hover:bg-accent-200 transition-colors"
                        >
                          {cat.name}
                        </Link>
                      )}
                      <span>{article.publishDate}</span>
                      <span>作者：{article.author}</span>
                      <span>{article.views} 阅读</span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
                      {article.title}
                    </h1>
                  </div>
                </div>
              </div>

              <div className="w-full px-4 md:px-6 mb-8">
                <div className="max-w-3xl mx-auto">
                  <div className="aspect-[16/9] rounded-lg overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="w-full px-4 md:px-6 pb-14">
                <div className="max-w-3xl mx-auto">
                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                      [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-950 [&_h3]:mt-8 [&_h3]:mb-3
                      [&_p]:mb-4 [&_p]:text-foreground-700
                      [&_strong]:text-foreground-950"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                </div>
              </div>
            </article>

            <div className="w-full px-4 md:px-6 py-10 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  相关推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {articles
                    .filter((a) => a.id !== article.id)
                    .slice(0, 3)
                    .map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.id}`}
                        className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline"
                      >
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={a.coverImage}
                            alt={a.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-3">
                          <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {a.title}
                          </h4>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (comic) {
    const comicUpdates = latestUpdates.filter((u) => u.comicId === comic.id);
    const cat = categories.find((c) => c.slug === comic.category);

    const formatViews = (v: number) => {
      if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
      return v.toString();
    };

    const relatedComics = comics
      .filter((c) => c.category === comic.category && c.id !== comic.id)
      .slice(0, 6);

    return (
      <>
        <SEOHead
          title={comic.title}
          description={comic.description}
          keywords={`${comic.title},${comic.tags.join(',')},韩漫,免费在线阅读韩漫,韩漫漫画网站`}
          canonicalUrl={`/article/${comic.id}`}
          ogType="article"
          ogImage={comic.coverImage}
        />
        <SchemaOrg type="Organization" />
        <SchemaOrg type="WebSite" />

        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="w-full px-4 md:px-6 py-6">
              <div className="max-w-5xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: cat?.name || '漫画', href: `/category/${comic.category}` },
                    { label: comic.title },
                  ]}
                />
              </div>
            </div>

            <div className="w-full px-4 md:px-6 py-6">
              <div className="max-w-5xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-shrink-0 w-full sm:w-64 mx-auto lg:mx-0">
                    <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={comic.coverImage}
                        alt={`${comic.title} - 免费在线阅读韩漫`}
                        title={`${comic.title} - 韩漫漫画网站`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                      {comic.title}
                    </h1>
                    <p className="text-sm text-foreground-500 mb-4">
                      作者：{comic.author}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="flex items-center gap-1 text-sm">
                        <i className="ri-star-fill text-accent-500"></i>
                        <strong className="text-foreground-950">{comic.rating}</strong>
                        <span className="text-foreground-400">评分</span>
                      </span>
                      <span className="text-sm text-foreground-400">
                        {formatViews(comic.views)} 阅读
                      </span>
                      <span className="text-sm text-foreground-400">
                        共 {comic.chapters} 话
                      </span>
                      {comic.status === 'ongoing' ? (
                        <span className="px-2.5 py-1 text-xs bg-primary-100 text-primary-700 rounded-full font-medium">
                          连载中
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full font-medium">
                          已完结
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {comic.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                      {cat && (
                        <Link
                          to={`/category/${cat.slug}`}
                          className="px-2.5 py-1 text-xs bg-accent-100 text-accent-700 rounded-full no-underline hover:bg-accent-200 transition-colors"
                        >
                          {cat.name}
                        </Link>
                      )}
                    </div>

                    <div className="bg-background-100 rounded-lg p-4 mb-5">
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {comic.description}
                      </p>
                    </div>

                    <a
                      href={`/category/${comic.category}`}
                      className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-300 cursor-pointer no-underline"
                    >
                      <i className="ri-book-open-line"></i>
                      开始阅读
                    </a>

                    {comicUpdates.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-base font-heading font-bold text-foreground-950 mb-3">
                          最新章节
                        </h3>
                        <div className="space-y-2">
                          {comicUpdates.map((update, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg"
                            >
                              <span className="text-sm text-foreground-700">
                                {update.chapter}：{update.title}
                              </span>
                              <span className="text-xs text-foreground-400">
                                {update.date}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="w-full px-4 md:px-6 py-10 bg-background-100">
                <div className="max-w-5xl mx-auto">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                    同类推荐
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {relatedComics.map((c) => (
                      <Link
                        key={c.id}
                        to={`/article/${c.id}`}
                        className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline"
                      >
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={c.coverImage}
                            alt={c.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-2">
                          <h4 className="text-xs font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                            {c.title}
                          </h4>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return null;
}